import { Box } from '@mui/material';
import Player from '@vimeo/player';
import React, { type SyntheticEvent, useEffect, useRef } from 'react';

import { borderRadius } from '../../assets/styles/themes/tokens';

export interface TimeUpdateProps {
	seconds: number;
	percent: number;
	duration: number;
}

interface VideoPlayerProps {
	url: string;
	onReady?: (value: boolean) => void;
	onPause?: (e: SyntheticEvent) => void;
	onPlay?: (e: SyntheticEvent) => void;
	onStreamed?: (data: TimeUpdateProps) => void;
}

const VideoPlayer: React.FC<VideoPlayerProps> = (props) => {
	const videoPlayer = useRef<HTMLDivElement>(null);

	// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
	useEffect(() => {
		const player = new Player('video-player', {
			url: props.url,
			responsive: true,
		});
		mountVideoPlayer(player);

		return () => {
			unmountVideoPlayer(player);
		};
	}, [props.url, props.onPause]);

	const mountVideoPlayer = (player: Player) => {
		props.onPause && player.on('pause', props.onPause);
		props.onPlay && player.on('play', props.onPlay);
		props.onStreamed && player.on('timeupdate', props.onStreamed);
		player.ready().then(() => {
			props.onReady && props.onReady(true);
		});
	};

	const unmountVideoPlayer = (player: Player) => {
		props.onReady && props.onReady(false);
		player.destroy().then(() => {
			if (videoPlayer?.current?.children[0])
				videoPlayer.current.removeChild(videoPlayer.current.children[0]);
		});
	};

	return (
		<Box
			data-testid="video-player"
			id="video-player"
			ref={videoPlayer}
			sx={{
				width: '100%',
				height: '100%',
				'& iframe': {
					height: 'auto',
					minHeight: 'auto',
					borderRadius: borderRadius.sm,
				},
			}}
		/>
	);
};

export default VideoPlayer;
